import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Sparkles, ChefHat, AlertTriangle, CheckCircle2, Clock,
  Loader2, RefreshCw, TrendingUp, Info, X, Plus, Minus, Check,
} from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../ui/dialog";
import {
  Tooltip, TooltipContent, TooltipTrigger, TooltipProvider,
} from "../ui/tooltip";
import { toast } from "sonner";
import api from "../../lib/api";

const fmt = (n, dec = 2) => {
  if (!n && n !== 0) return "—";
  const v = Number(n);
  return Number.isFinite(v) ? v.toFixed(dec).replace(/\.?0+$/, "") : "—";
};

// ── Row: one editable recommendation ──────────────────────────────────
function RecommendationRow({ rec, qty, onQtyChange }) {
  const urgentColor = rec.needs_production
    ? "border-l-4 border-l-amber-400"
    : "border-l-4 border-l-emerald-400";

  return (
    <div
      className={`flex items-center gap-3 p-3 rounded-lg bg-card border ${urgentColor} mb-2`}
      data-testid={`rec-row-${rec.batch_recipe_id}`}
    >
      {/* Status icon */}
      <div className="flex-shrink-0">
        {rec.needs_production
          ? <AlertTriangle className="w-4 h-4 text-amber-500" />
          : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
      </div>

      {/* Name + meta */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{rec.name}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-[11px] text-muted-foreground">
            Media: <strong>{fmt(rec.avg_daily_consumption)} {rec.unit}/día</strong>
          </span>
          {rec.shelf_life_days && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5">
              <Clock className="w-2.5 h-2.5 mr-1" />
              {rec.shelf_life_days}d caducidad
            </Badge>
          )}
          {rec.last_produced_at && (
            <span className="text-[11px] text-muted-foreground">
              · Último: {rec.last_produced_at}
              {rec.days_since_production !== null && (
                <span className={rec.needs_production ? " text-amber-700 font-medium" : ""}>
                  {" "}({rec.days_since_production}d)
                </span>
              )}
            </span>
          )}
          {!rec.last_produced_at && (
            <span className="text-[11px] text-amber-700">· Sin historial</span>
          )}
        </div>
      </div>

      {/* Quantity stepper */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={() => onQtyChange(rec.batch_recipe_id, Math.max(0, qty - (rec.recommended_qty * 0.1 || 0.1)))}
          className="w-6 h-6 rounded border flex items-center justify-center hover:bg-muted text-muted-foreground"
        >
          <Minus className="w-3 h-3" />
        </button>
        <input
          type="number"
          min={0}
          step={0.1}
          value={qty}
          onChange={(e) => onQtyChange(rec.batch_recipe_id, parseFloat(e.target.value) || 0)}
          className="w-20 h-7 text-center text-sm border rounded focus:outline-none focus:ring-1 focus:ring-ring"
          data-testid={`rec-qty-${rec.batch_recipe_id}`}
        />
        <button
          onClick={() => onQtyChange(rec.batch_recipe_id, qty + (rec.recommended_qty * 0.1 || 0.1))}
          className="w-6 h-6 rounded border flex items-center justify-center hover:bg-muted text-muted-foreground"
        >
          <Plus className="w-3 h-3" />
        </button>
        <span className="text-xs text-muted-foreground w-8">{rec.unit}</span>
      </div>
    </div>
  );
}

// ── Confirm dialog ──────────────────────────────────────────────────
function ConfirmProductionDialog({ open, onClose, restaurantId, items, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");

  const handleConfirm = async () => {
    if (items.length === 0) return;
    setSaving(true);
    try {
      const productionItems = items.map(it => ({
        name: it.name,
        qty: parseFloat(it.qty) || 0,
        unit: it.unit,
        shelf_life_days: it.shelf_life_days || null,
        link_type: "batch_recipe",
        linked_id: it.batch_recipe_id,
        link_status: "linked",
        notes: `Producción automática — recomendado: ${fmt(it.recommended_qty)} ${it.unit}`,
      }));
      await api.upsertProductionLog(restaurantId, {
        items: productionItems,
        wasteItems: [],
        notes: notes || "Cierre de producción confirmado desde panel de recomendaciones",
        day: new Date().toISOString().slice(0, 10),
      });
      toast.success(`Producción de hoy confirmada · ${productionItems.length} elaboraciones`);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.message || "Error al guardar la producción");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="confirm-production-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-primary" />
            Confirmar producción de hoy
          </DialogTitle>
          <DialogDescription>
            {items.length} elaboración{items.length !== 1 ? "es" : ""} se registrarán y se descontarán del inventario.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
          {items.map(it => (
            <div
              key={it.batch_recipe_id}
              className="flex items-center justify-between py-1.5 border-b last:border-0 gap-2"
            >
              <span className="text-sm truncate flex-1">{it.name}</span>
              <span className="text-sm font-medium flex-shrink-0 tabular-nums">
                {fmt(it.qty)} <span className="text-muted-foreground text-xs">{it.unit}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="pt-1">
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Notas opcionales sobre la producción de hoy..."
            rows={2}
            className="w-full text-sm border rounded-lg p-2 resize-none focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="production-notes-input"
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleConfirm} disabled={saving} data-testid="confirm-production-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}
            Confirmar producción
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main panel ──────────────────────────────────────────────────────
export default function ProductionRecommendationsPanel({ restaurantId, onProductionConfirmed }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [daysWindow, setDaysWindow] = useState(7);
  const [qtys, setQtys] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showUntraced, setShowUntraced] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!restaurantId) return;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/production-recommendations?days=${daysWindow}`
      );
      setData(res);
      // Pre-fill qtys with recommendations
      const init = {};
      for (const r of res.recommendations || []) {
        init[r.batch_recipe_id] = r.recommended_qty;
      }
      setQtys(init);
    } catch {
      toast.error("No se pudieron cargar las recomendaciones");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [restaurantId, daysWindow]);

  useEffect(() => { load(); }, [load]);

  const handleQtyChange = useCallback((brid, val) => {
    setQtys(prev => ({ ...prev, [brid]: Math.max(0, Math.round(val * 100) / 100) }));
  }, []);

  // Items to confirm: only those with qty > 0
  const itemsToConfirm = useMemo(() => {
    return (data?.recommendations || [])
      .filter(r => (qtys[r.batch_recipe_id] || 0) > 0)
      .map(r => ({ ...r, qty: qtys[r.batch_recipe_id] }));
  }, [data, qtys]);

  const urgentCount = (data?.recommendations || []).filter(r => r.needs_production).length;
  const okCount = (data?.recommendations || []).filter(r => !r.needs_production).length;

  if (loading) {
    return (
      <Card data-testid="production-recommendations-panel">
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.recommendations.length === 0) {
    return (
      <Card data-testid="production-recommendations-panel">
        <CardContent className="p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <p className="font-semibold text-sm">Recomendaciones de producción</p>
            </div>
            <select
              value={daysWindow}
              onChange={e => setDaysWindow(Number(e.target.value))}
              className="text-xs border rounded px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-ring"
              data-testid="days-window-select"
            >
              {[3, 7, 14, 30].map(d => <option key={d} value={d}>Últimos {d} días</option>)}
            </select>
          </div>
          <p className="text-sm text-muted-foreground">
            {data?.untraced_dish_count > 0
              ? `No se encontraron recetas base vinculadas a los platos vendidos. ${data.untraced_dish_count} plato(s) sin trazar.`
              : `Sin ventas en los últimos ${daysWindow} días para calcular recomendaciones.`}
          </p>
          {data?.untraced_dish_count > 0 && (
            <p className="text-xs text-amber-700 mt-1">
              Asigna recetas profesionales con elaboraciones base para activar las recomendaciones.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card data-testid="production-recommendations-panel">
        <CardContent className="p-5 space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              <div>
                <p className="font-semibold text-sm">Producción recomendada para hoy</p>
                <p className="text-xs text-muted-foreground">
                  Basado en ventas de los últimos{" "}
                  <select
                    value={daysWindow}
                    onChange={e => setDaysWindow(Number(e.target.value))}
                    className="border-0 underline underline-offset-2 bg-transparent text-xs cursor-pointer focus:outline-none"
                    data-testid="days-window-select"
                  >
                    {[3, 7, 14, 30].map(d => <option key={d} value={d}>{d} días</option>)}
                  </select>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {urgentCount > 0 && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {urgentCount} urgente{urgentCount > 1 ? "s" : ""}
                </Badge>
              )}
              {okCount > 0 && (
                <Badge variant="secondary" className="text-[10px]">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  {okCount} al día
                </Badge>
              )}
              <Button
                variant="ghost" size="icon"
                onClick={() => load(true)}
                disabled={refreshing}
                className="h-7 w-7"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>

          {/* Untraced warning */}
          {(data?.untraced_dish_count || 0) > 0 && (
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
              <Info className="w-3.5 h-3.5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  <strong>{data.untraced_dish_count} plato(s) sin trazar</strong> — no tienen receta profesional con elaboraciones base.
                  Las recomendaciones son parciales.
                </p>
                {data.untraced_dishes?.length > 0 && (
                  <button
                    className="text-[10px] text-amber-700 underline mt-0.5"
                    onClick={() => setShowUntraced(v => !v)}
                  >
                    {showUntraced ? "Ocultar" : "Ver platos sin trazar"}
                  </button>
                )}
                {showUntraced && (
                  <ul className="mt-1 space-y-0.5">
                    {data.untraced_dishes.map(d => (
                      <li key={d.dish_id} className="text-[10px] text-amber-700">· {d.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Recommendations list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Elaboraciones base
              </p>
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button className="text-muted-foreground">
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left" className="max-w-xs text-xs">
                    Las cantidades son una sugerencia. Ajústalas si lo necesitas antes de confirmar.
                    🟡 = producción pendiente según caducidad · 🟢 = stock presumiblemente vigente
                  </TooltipContent>
                </Tooltip>
                <button
                  className="text-xs text-muted-foreground underline"
                  onClick={() => {
                    const reset = {};
                    for (const r of data.recommendations) reset[r.batch_recipe_id] = r.recommended_qty;
                    setQtys(reset);
                  }}
                >
                  Restablecer
                </button>
              </div>
            </div>
            {data.recommendations.map(rec => (
              <RecommendationRow
                key={rec.batch_recipe_id}
                rec={rec}
                qty={qtys[rec.batch_recipe_id] ?? rec.recommended_qty}
                onQtyChange={handleQtyChange}
              />
            ))}
          </div>

          {/* Confirm button */}
          <div className="pt-2 border-t flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {itemsToConfirm.length} de {data.recommendations.length} elaboración{data.recommendations.length !== 1 ? "es" : ""} a confirmar
            </p>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={itemsToConfirm.length === 0}
              data-testid="open-confirm-production-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              Cerrar producción de hoy
            </Button>
          </div>
        </CardContent>
      </Card>

      <ConfirmProductionDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        restaurantId={restaurantId}
        items={itemsToConfirm}
        onSuccess={() => { onProductionConfirmed?.(); load(true); }}
      />
    </TooltipProvider>
  );
}
